<template>
  <div class="backtest-chart">
    <div class="chart-controls">
      <div class="control-group">
        <el-select
          v-model="selectedContractor"
          class="control-select"
          size="small"
        >
          <el-option label="Contractor 0" value="0"></el-option>
          <el-option label="Contractor 1" value="1"></el-option>
          <el-option label="Contractor 2" value="2"></el-option>
        </el-select>
      </div>
      <div class="control-group">
        <el-select
          v-model="selectedColumns"
          multiple
          collapse-tags
          collapse-tags-tooltip
          placeholder="Select parameters"
          class="control-select"
          size="small"
        >
          <el-option
            v-for="column in availableColumns"
            :key="column"
            :label="column"
            :value="column"
          ></el-option>
        </el-select>
      </div>
      <div
        class="cycle-properties"
        v-if="
          cycleData &&
            (cycleData.PadWeight !== undefined ||
              cycleData.FinalVolume !== undefined)
        "
      >
        <div v-if="cycleData.PadWeight !== undefined" class="property-item">
          <label>Pad Weight:</label>
          <span>{{
            cycleData.PadWeight === 0 ? 'Not provided' : cycleData.PadWeight
          }}</span>
        </div>
        <div v-if="cycleData.FinalVolume !== undefined" class="property-item">
          <label>Final Volume:</label>
          <span>{{ cycleData.FinalVolume }}</span>
        </div>
      </div>
      <div class="chart-action-buttons">
        <el-button
          v-if="filterColumns.length > 0"
          @click="toggleFilter"
          size="small"
          :type="showFilter ? 'primary' : 'default'"
        >
          <i
            :class="showFilter ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
          ></i>
          {{ showFilter ? 'Hide Filter' : 'Show Filter' }}
        </el-button>
        <el-button
          @click="$emit('download')"
          :disabled="downloadDisabled"
          type="primary"
          size="small"
        >
          Download Chart Data
        </el-button>
        <el-button @click="resetZoom" size="small">
          Reset Zoom
        </el-button>
      </div>
    </div>

    <!-- Global Data Filter (collapsible) -->
    <div v-if="showFilter && filterColumns.length > 0" class="filter-section">
      <BackTestDataFilter
        :availableColumns="filterColumns"
        :data="filterData"
        @filter-change="handleFilterChange"
        ref="globalFilter"
      />
    </div>

    <div
      v-loading="loading"
      element-loading-text="Loading chart data..."
      element-loading-background="rgba(255, 255, 255, 0.9)"
      ref="chartContainer"
      class="chart-container"
      :style="{ height: chartHeight + 'px' }"
    ></div>
  </div>
</template>

<script>
import echarts from 'echarts'
import moment from 'moment'
import BackTestApiService from '@/services/BackTestApiService'
import BackTestDataFilter from './BackTestDataFilter.vue'
import swal from 'sweetalert'
import { jobTypes } from '@/const'

export default {
  name: 'BackTestChart',
  components: {
    BackTestDataFilter
  },
  props: {
    chartUrl: {
      type: String,
      required: true
    },
    jobId: {
      type: [String, Number],
      required: true
    },
    oldTimeStamps: {
      type: Array,
      default: () => []
    },
    newTimeStamps: {
      type: Array,
      default: () => []
    },
    s1Events: {
      type: Array,
      default: () => []
    },
    s2Events: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Array,
      default: () => []
    },
    chartHeight: {
      type: Number,
      default: 500
    },
    cycleData: {
      type: Object,
      default: null
    },
    jobType: {
      type: String,
      default: null
    },
    filterColumns: {
      type: Array,
      default: () => []
    },
    filterData: {
      type: Array,
      default: () => []
    },
    downloadDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chart: null,
      loading: false,
      chartData: null,
      selectedContractor: '1',
      selectedColumns: ['Sensor1Value', 'Sensor2Value'],
      availableColumns: [],
      initChartTimer: null,
      showFilter: false,
      colorPalette: [
        '#5470c6',
        '#91cc75',
        '#fac858',
        '#ee6666',
        '#73c0de',
        '#3ba272',
        '#fc8452',
        '#9a60b4',
        '#ea7ccc',
        '#5d7092',
        '#91cc75',
        '#fac858',
        '#73a9ff',
        '#9ac1ff',
        '#b5e7a0'
      ]
    }
  },
  watch: {
    chartUrl: {
      immediate: true,
      handler() {
        this.loadChartData()
      }
    },
    oldTimeStamps() {
      if (this.chartData && !this.loading) {
        this.debouncedInitChart()
      }
    },
    newTimeStamps() {
      if (this.chartData && !this.loading) {
        this.debouncedInitChart()
      }
    },
    selectedContractor() {
      if (this.chartData) {
        this.debouncedInitChart()
      }
    },
    selectedColumns: {
      deep: true,
      handler() {
        if (this.chartData) {
          this.debouncedInitChart()
        }
      }
    },
    jobType() {
      // Update default columns when job type changes
      this.selectedColumns = this.getDefaultColumns()
    }
  },
  mounted() {
    // Set initial columns based on job type
    this.selectedColumns = this.getDefaultColumns()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.initChartTimer) {
      clearTimeout(this.initChartTimer)
    }
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    getDefaultColumns() {
      // Set default columns based on job type
      if (this.jobType === jobTypes.backTestingVolumeEstimation) {
        return ['Sensor1Value', 'Sensor2Value', 'Volume']
      }
      return ['Sensor1Value', 'Sensor2Value']
    },
    debouncedInitChart() {
      // Clear any pending chart initialization
      if (this.initChartTimer) {
        clearTimeout(this.initChartTimer)
      }
      // Schedule chart initialization with 300ms debounce
      this.initChartTimer = setTimeout(() => {
        this.initChart()
      }, 300)
    },
    normalizeSeriesName(name) {
      return (name || '').replace(/\s+/g, '').toLowerCase()
    },
    isRollingS1Mean(name) {
      return this.normalizeSeriesName(name) === 'rollings1mean'
    },
    isRollingS2Mean(name) {
      return this.normalizeSeriesName(name) === 'rollings2mean'
    },
    findNearestPoint(seriesData, targetTs) {
      if (!Array.isArray(seriesData) || seriesData.length === 0) return null
      let closest = seriesData[0]
      let minDiff = Math.abs(seriesData[0][0] - targetTs)
      for (let i = 1; i < seriesData.length; i++) {
        const diff = Math.abs(seriesData[i][0] - targetTs)
        if (diff < minDiff) {
          minDiff = diff
          closest = seriesData[i]
        }
      }
      return closest
    },
    buildEventMarkers(events, seriesData, colors, labelPrefix) {
      const markAreas = []
      const markPoints = []
      if (!Array.isArray(events) || events.length === 0) {
        return { markAreas, markPoints }
      }

      events.forEach((event, index) => {
        const startTime = this.parseLocalTimestamp(event.StartTime)
        const endTime = this.parseLocalTimestamp(event.EndTime)
        if (isNaN(startTime) || isNaN(endTime)) {
          return
        }

        const stepNumber =
          event.StepNumber != null ? event.StepNumber : index + 1
        const label = `${labelPrefix} Step ${stepNumber}`

        markAreas.push([
          {
            name: label,
            xAxis: startTime,
            itemStyle: { color: colors.area }
          },
          {
            xAxis: endTime,
            itemStyle: { color: colors.area }
          }
        ])

        const startPoint = this.findNearestPoint(seriesData, startTime)
        if (startPoint) {
          markPoints.push({
            name: `${label} Start`,
            coord: [startPoint[0], startPoint[1]],
            itemStyle: { color: colors.start },
            symbolSize: 6,
            label: { show: false },
            eventLabel: label,
            eventTime: startTime,
            eventType: 'Start'
          })
        }

        const endPoint = this.findNearestPoint(seriesData, endTime)
        if (endPoint) {
          markPoints.push({
            name: `${label} End`,
            coord: [endPoint[0], endPoint[1]],
            itemStyle: { color: colors.end },
            symbolSize: 6,
            label: { show: false },
            eventLabel: label,
            eventTime: endTime,
            eventType: 'End'
          })
        }
      })

      return { markAreas, markPoints }
    },
    parseColumns(data) {
      if (!data || data.length === 0)
        return { numericColumns: [], timestampColumn: null }

      const firstRow = data[0]
      const ignoredColumnsLower = [
        'localtimestamp',
        'timestamp',
        'contractorid',
        'podmac',
        'podstatus',
        'nodemac',
        'hwversion',
        'firmwareversion',
        'isnewpad',
        'isnewredalert',
        'ispositionchanged',
        'position'
      ]
      const numericColumns = []
      const timestampColumn = 'LocalTimeStamp'

      Object.keys(firstRow).forEach(key => {
        const keyLower = String(key || '').toLowerCase()
        if (ignoredColumnsLower.includes(keyLower)) {
          return
        }
        const value = parseFloat(firstRow[key])
        if (!isNaN(value)) {
          numericColumns.push(key)
        }
      })

      return { numericColumns, timestampColumn }
    },

    applyFilters(data) {
      if (!Array.isArray(this.filters) || this.filters.length === 0) {
        return data
      }

      return data.filter(row => {
        return this.filters.every(filter => {
          if (!filter.column || !filter.value) return true

          const cellValue = row[filter.column]
          if (cellValue === null || cellValue === undefined) return true

          // Try to parse as number for numeric columns
          const numValue = parseFloat(cellValue)
          const filterNum = parseFloat(filter.value)

          if (!isNaN(numValue) && !isNaN(filterNum)) {
            // Numeric comparison
            switch (filter.operator) {
              case '=':
                return numValue === filterNum
              case '>':
                return numValue > filterNum
              case '>=':
                return numValue >= filterNum
              case '<':
                return numValue < filterNum
              case '<=':
                return numValue <= filterNum
              default:
                return true
            }
          } else {
            // String comparison
            return String(cellValue)
              .toLowerCase()
              .includes(filter.value.toLowerCase())
          }
        })
      })
    },

    generateDynamicSeries(data) {
      // Apply filters first
      const filteredData = this.applyFilters(data)

      const { numericColumns, timestampColumn } = this.parseColumns(
        filteredData
      )

      if (numericColumns.length === 0) {
        return []
      }

      const contractorData = filteredData.filter(
        row => parseInt(row.ContractorId) === parseInt(this.selectedContractor)
      )

      const columnsToDisplay = [...this.selectedColumns]

      const series = []

      columnsToDisplay.forEach((column, index) => {
        if (!numericColumns.includes(column)) {
          return
        }

        const color = this.colorPalette[index % this.colorPalette.length]

        const columnValues = contractorData.map(row => {
          const timestamp = moment(
            row[timestampColumn],
            'MM/DD/YYYY HH:mm:ss'
          ).valueOf()
          const value = parseFloat(row[column] || 0)
          return [timestamp, value]
        })

        if (columnValues.length > 0) {
          series.push({
            name: column,
            type: 'line',
            smooth: true,
            symbol: 'none',
            itemStyle: { color },
            lineStyle: { color, width: 2 },
            data: columnValues
          })
        }
      })

      return series
    },

    extractPositionChangeMarkers(data) {
      // Extract position change markers from data
      const markers = []

      if (!data || data.length === 0) {
        return markers
      }

      const contractorData = data.filter(
        row => parseInt(row.ContractorId) === parseInt(this.selectedContractor)
      )

      contractorData.forEach(row => {
        // Check if IsPositionChanged exists and equals 1 (or true for boolean)
        const isPositionChanged = row.IsPositionChanged || row.isPositionChanged
        const isChanged =
          isPositionChanged === true ||
          isPositionChanged === 1 ||
          isPositionChanged === '1' ||
          String(isPositionChanged).toUpperCase() === 'TRUE'
        if (isChanged) {
          const timestamp = moment(
            row.LocalTimeStamp,
            'MM/DD/YYYY HH:mm:ss'
          ).valueOf()
          if (isNaN(timestamp)) {
            return
          }
          const position = row.Position || row.position || 'Unknown'

          markers.push({
            name: 'Position Change',
            value: [timestamp, 0],
            label: `Position: ${position}`,
            position: position,
            timestamp: timestamp,
            itemStyle: {
              color: '#9a60b4'
            }
          })
        }
      })

      return markers
    },

    adjustColor(color, amount) {
      const clamp = num => Math.min(255, Math.max(0, num))
      const num = parseInt(color.replace('#', ''), 16)
      const r = clamp((num >> 16) + amount)
      const g = clamp(((num >> 8) & 0x00ff) + amount)
      const b = clamp((num & 0x0000ff) + amount)
      return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)
    },

    async loadChartData() {
      if (!this.chartUrl) {
        return
      }

      this.loading = true
      try {
        const result = await BackTestApiService.fetchChartData(this.chartUrl)
        this.chartData = result

        if (result && Array.isArray(result.data) && result.data.length > 0) {
          const { numericColumns } = this.parseColumns(result.data)
          this.availableColumns = [...numericColumns]
          const filtered = this.selectedColumns.filter(col =>
            this.availableColumns.includes(col)
          )
          if (
            filtered.length !== this.selectedColumns.length ||
            !filtered.every((c, i) => c === this.selectedColumns[i])
          ) {
            this.selectedColumns = filtered
          }

          // Emit chart loaded event with available columns and raw data
          this.$emit('chart-loaded', {
            availableColumns: this.availableColumns,
            rawData: result.data
          })
        }

        this.loading = false

        await this.$nextTick()

        this.debouncedInitChart()
      } catch (error) {
        this.loading = false
        swal({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load chart data'
        })
      }
    },
    initChart() {
      if (!this.$refs.chartContainer) {
        return
      }

      // Dispose existing chart
      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(this.$refs.chartContainer)

      let dynamicSeries = []
      let timestamps = []

      if (this.chartData && Array.isArray(this.chartData.data)) {
        dynamicSeries = this.generateDynamicSeries(this.chartData.data)

        const contractorData = this.chartData.data.filter(
          row =>
            parseInt(row.ContractorId) === parseInt(this.selectedContractor)
        )
        timestamps = contractorData.map(row => {
          return moment(row.LocalTimeStamp, 'MM/DD/YYYY HH:mm:ss').valueOf()
        })
      }

      // Create markers from oldTimeStamps and newTimeStamps
      // Position markers at the bottom of the chart (y=0)
      const markerY = 0

      const oldMarkers = this.oldTimeStamps
        .map(ts => {
          const timestamp = this.parseLocalTimestamp(ts)
          if (isNaN(timestamp)) {
            return null
          }
          return {
            name: 'Old',
            value: [timestamp, markerY],
            label: ts
          }
        })
        .filter(marker => marker !== null)

      const newMarkers = this.newTimeStamps
        .map(ts => {
          const timestamp = this.parseLocalTimestamp(ts)
          if (isNaN(timestamp)) {
            return null
          }
          return {
            name: 'New',
            value: [timestamp, markerY],
            label: ts
          }
        })
        .filter(marker => marker !== null)

      // Extract position change markers
      const positionChangeMarkers =
        this.chartData && Array.isArray(this.chartData.data)
          ? this.extractPositionChangeMarkers(this.chartData.data)
          : []

      // Chart options
      const seriesWithEvents = dynamicSeries.map(series => {
        if (this.isRollingS1Mean(series.name)) {
          const markers = this.buildEventMarkers(
            this.s1Events,
            series.data,
            {
              area: 'rgba(144, 238, 144, 0.2)',
              start: '#90EE90',
              end: '#228B22'
            },
            'S1'
          )
          return {
            ...series,
            markArea: { data: markers.markAreas },
            markPoint: {
              data: markers.markPoints,
              tooltip: {
                formatter: params => {
                  const data = params.data || {}
                  const time = data.eventTime
                    ? moment(data.eventTime).format('YYYY-MM-DD HH:mm:ss')
                    : ''
                  return `${data.eventLabel || ''} ${data.eventType ||
                    ''}<br/>${time}`
                }
              }
            }
          }
        }

        if (this.isRollingS2Mean(series.name)) {
          const markers = this.buildEventMarkers(
            this.s2Events,
            series.data,
            {
              area: 'rgba(173, 216, 230, 0.2)',
              start: '#ADD8E6',
              end: '#00008B'
            },
            'S2'
          )
          return {
            ...series,
            markArea: { data: markers.markAreas },
            markPoint: {
              data: markers.markPoints,
              tooltip: {
                formatter: params => {
                  const data = params.data || {}
                  const time = data.eventTime
                    ? moment(data.eventTime).format('YYYY-MM-DD HH:mm:ss')
                    : ''
                  return `${data.eventLabel || ''} ${data.eventType ||
                    ''}<br/>${time}`
                }
              }
            }
          }
        }

        return series
      })

      const option = {
        title: {
          text: 'Sensor Data',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: params => {
            if (!params || params.length === 0 || !params[0].value) {
              return ''
            }
            try {
              const xAxisValue = params[0].value[0]
              const timeStr = moment(xAxisValue).format('YYYY-MM-DD HH:mm:ss')
              let content = `<strong>${timeStr}</strong><br/>`
              params.forEach(param => {
                if (param.componentType === 'series') {
                  if (param.seriesName === 'Old Algorithm') {
                    content += `Old Algorithm Triggered<br/>`
                  } else if (param.seriesName === 'New Algorithm') {
                    content += `New Algorithm Triggered<br/>`
                  } else if (param.seriesName === 'Position Change') {
                    content += `Position Change：${param.data.position ||
                      'Unknown'}<br/>`
                  } else {
                    content += `${param.seriesName}: ${param.value[1].toFixed(
                      2
                    )}<br/>`
                  }
                }
              })
              return content
            } catch (e) {
              return 'Invalid data'
            }
          }
        },
        legend: {
          data: [
            ...dynamicSeries.map(s => s.name),
            'Old Algorithm',
            'New Algorithm',
            'Position Change'
          ],
          top: 30,
          type: 'scroll',
          pageButtonItemGap: 5,
          pageIconSize: 10
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '80px',
          containLabel: true
        },
        toolbox: {
          feature: {
            dataZoom: {
              yAxisIndex: 'none'
            },
            restore: {},
            saveAsImage: {}
          }
        },
        xAxis: {
          type: 'time',
          boundaryGap: false,
          axisLabel: {
            formatter: value => {
              return moment(value).format('MM-DD HH:mm:ss')
            }
          }
        },
        yAxis: {
          type: 'value'
        },
        dataZoom: [
          {
            type: 'inside',
            start: 0,
            end: 100
          },
          {
            start: 0,
            end: 100,
            handleSize: '80%'
          }
        ],
        series: [
          ...seriesWithEvents,
          {
            name: 'Old Algorithm',
            type: 'scatter',
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#ee6666'
            },
            label: {
              show: false
            },
            data: oldMarkers,
            tooltip: {
              formatter: params => {
                return `<strong>${moment(params.value[0]).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}</strong><br/>Old Algorithm Triggered`
              }
            },
            legendHoverLink: true,
            emphasis: {
              itemStyle: {
                color: '#cc4444'
              }
            }
          },
          {
            name: 'New Algorithm',
            type: 'scatter',
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: {
              color: '#fac858'
            },
            label: {
              show: false
            },
            data: newMarkers,
            tooltip: {
              formatter: params => {
                return `<strong>${moment(params.value[0]).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}</strong><br/>New Algorithm Triggered`
              }
            },
            legendHoverLink: true,
            emphasis: {
              itemStyle: {
                color: '#d9a636'
              }
            }
          },
          {
            name: 'Position Change',
            type: 'scatter',
            symbol: 'diamond',
            symbolSize: 6,
            itemStyle: {
              color: '#9a60b4'
            },
            label: {
              show: false,
              position: 'top',
              formatter: params => {
                return params.data.position || ''
              },
              fontSize: 10,
              color: '#9a60b4'
            },
            data: positionChangeMarkers,
            tooltip: {
              formatter: params => {
                return `<strong>${moment(params.value[0]).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}</strong><br/>Position Changed to: ${params.data.position ||
                  'Unknown'}`
              }
            },
            legendHoverLink: true,
            emphasis: {
              itemStyle: {
                color: '#7a4094'
              }
            }
          }
        ]
      }

      this.chart.setOption(option)

      // Emit initial full-range selection for table load
      if (timestamps.length > 0) {
        const startTime = moment(timestamps[0]).format('YYYY-MM-DD HH:mm:ss')
        const endTime = moment(timestamps[timestamps.length - 1]).format(
          'YYYY-MM-DD HH:mm:ss'
        )

        this.$emit('selection-changed', {
          startTime,
          endTime
        })
      }

      // Handle brush/zoom selection
      this.chart.on('datazoom', () => {
        const option = this.chart.getOption()
        const dataZoom = option.dataZoom[0]

        if (
          dataZoom.startValue !== undefined &&
          dataZoom.endValue !== undefined
        ) {
          // startValue and endValue are timestamps in milliseconds
          const startTime = moment(dataZoom.startValue).format(
            'YYYY-MM-DD HH:mm:ss'
          )
          const endTime = moment(dataZoom.endValue).format(
            'YYYY-MM-DD HH:mm:ss'
          )

          this.$emit('selection-changed', {
            startTime,
            endTime
          })
        }
      })
    },
    parseLocalTimestamp(value) {
      if (!value) return NaN
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed.endsWith('Z')) {
          const withoutZ = trimmed.slice(0, -1)
          const mNoZone = moment(withoutZ, moment.ISO_8601, true)
          if (mNoZone.isValid()) return mNoZone.valueOf()
        }
        const mIso = moment(trimmed, moment.ISO_8601, true)
        if (mIso.isValid()) return mIso.valueOf()
        const mLocal = moment(trimmed, 'MM/DD/YYYY HH:mm:ss', true)
        if (mLocal.isValid()) return mLocal.valueOf()
      }
      const mFallback = moment(value)
      return mFallback.isValid() ? mFallback.valueOf() : NaN
    },
    findClosestIndex(timestamps, targetTimestamp) {
      if (!timestamps || timestamps.length === 0) return -1

      let closest = 0
      let minDiff = Math.abs(timestamps[0] - targetTimestamp)

      for (let i = 1; i < timestamps.length; i++) {
        const diff = Math.abs(timestamps[i] - targetTimestamp)
        if (diff < minDiff) {
          minDiff = diff
          closest = i
        }
      }

      return closest
    },
    resetZoom() {
      if (this.chart) {
        this.chart.dispatchAction({
          type: 'dataZoom',
          start: 0,
          end: 100
        })
      }
    },
    toggleFilter() {
      this.showFilter = !this.showFilter
    },
    handleFilterChange(filters) {
      this.$emit('filter-change', filters)
    },
    clearAllFilters() {
      if (this.$refs.globalFilter) {
        this.$refs.globalFilter.clearAllFilters()
      }
    },
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.backtest-chart {
  position: relative;

  .chart-controls {
    display: flex;
    gap: 8px;
    padding-bottom: 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    align-items: center;
    flex-wrap: wrap;

    .control-group {
      display: flex;
      flex-direction: column;
      gap: 6px;

      label {
        font-size: 13px;
        font-weight: 600;
        color: #333;
      }

      .control-select {
        width: 100%;
        min-width: 200px;
      }
    }

    .cycle-properties {
      display: flex;
      gap: 20px;
      align-items: center;
      margin-left: auto;
      padding: 0 8px;

      .property-item {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          font-size: 13px;
          font-weight: 600;
          color: #555;
          margin: 0;
          white-space: nowrap;
        }

        span {
          font-size: 13px;
          color: #333;
        }
      }
    }

    .chart-action-buttons {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-left: auto;
      .el-button {
        margin-left: 0;
      }
    }
  }

  .filter-section {
    padding: 10px 0;
    border-top: 1px solid #eee;
    margin-bottom: 5px;
  }

  .chart-container {
    position: relative;
    width: 100%;
    min-height: 400px;
  }
}
</style>
